/**
 * NotificationSettings — the notifications card on the Profile page.
 *
 * Two things: whether this browser receives push (one device at a time is
 * registered from here; other devices register themselves), and which
 * optional categories the seeker wants. Billing cannot be turned off; it
 * changes their access, so it is shown as always on.
 *
 * Every string comes from the `notifications` namespace of translation.json.
 * The push flow is the package's client helper; the Edge Function stores
 * the result.
 */
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  currentEndpoint,
  disablePushOnThisDevice,
  enablePushOnThisDevice,
  fetchNotificationState,
  pushAvailable,
  setPreference,
  type PreferenceRow,
} from '../lib/notifications/client'

type DeviceState = 'checking' | 'unsupported' | 'denied' | 'off' | 'enabling' | 'on' | 'failed'

export default function NotificationSettings() {
  const { t } = useTranslation()
  const [device, setDevice] = useState<DeviceState>('checking')
  const [vapidKey, setVapidKey] = useState<string | null>(null)
  const [prefs, setPrefs] = useState<PreferenceRow[]>([])
  const [savedCategory, setSavedCategory] = useState<string | null>(null)
  const [unavailable, setUnavailable] = useState(false)

  useEffect(() => {
    let isCancelled = false
    async function load() {
      try {
        const state = await fetchNotificationState()
        if (isCancelled) return
        setVapidKey(state.vapidPublicKey)
        setPrefs(state.preferences)
        if (!pushAvailable(state.vapidPublicKey)) return setDevice('unsupported')
        if (Notification.permission === 'denied') return setDevice('denied')
        const endpoint = await currentEndpoint()
        if (isCancelled) return
        setDevice(endpoint && state.endpoints.includes(endpoint) ? 'on' : 'off')
      } catch {
        if (!isCancelled) setUnavailable(true)
      }
    }
    void load()
    return () => {
      isCancelled = true
    }
  }, [])

  async function enable() {
    if (!vapidKey) return
    setDevice('enabling')
    try {
      setDevice(await enablePushOnThisDevice(vapidKey))
    } catch {
      setDevice('failed')
    }
  }

  async function disable() {
    try {
      await disablePushOnThisDevice()
      setDevice('off')
    } catch {
      setDevice('failed')
    }
  }

  async function toggle(category: string, enabled: boolean) {
    setPrefs(rows => rows.map(r => (r.category === category ? { ...r, enabled } : r)))
    try {
      setPrefs(await setPreference(category, enabled))
      setSavedCategory(category)
      setTimeout(() => setSavedCategory(null), 1500)
    } catch {
      setPrefs(rows => rows.map(r => (r.category === category ? { ...r, enabled: !enabled } : r)))
    }
  }

  if (unavailable) return null

  return (
    <div className="rounded-2xl px-5 py-5" style={{ background: '#FFFFFF', border: '1.5px solid #E4D5EE' }}>
      <p className="text-xs font-medium uppercase tracking-widest mb-3" style={{ color: '#9B72C0' }}>
        {t('notifications.heading')}
      </p>

      {/* This device */}
      <p className="text-sm" style={{ color: '#1C1128' }}>{t('notifications.deviceTitle')}</p>
      <p className="text-xs mt-0.5 mb-3" style={{ color: '#9B8FAA', lineHeight: 1.5 }}>{t('notifications.deviceBody')}</p>
      {device === 'unsupported' && <p className="text-xs" style={{ color: '#9B8FAA' }}>{t('notifications.unsupported')}</p>}
      {device === 'denied' && <p className="text-xs" style={{ color: '#9B8FAA' }}>{t('notifications.denied')}</p>}
      {device === 'failed' && <p className="text-xs mb-2" style={{ color: '#C45272' }}>{t('notifications.failed')}</p>}
      {(device === 'off' || device === 'failed') && (
        <button
          type="button"
          onClick={() => void enable()}
          className="text-sm font-medium px-4 py-2 rounded-full transition-opacity hover:opacity-80"
          style={{ background: '#3D1F62', color: '#FFFFFF', border: 'none', cursor: 'pointer' }}
        >
          {t('notifications.enable')}
        </button>
      )}
      {device === 'enabling' && <p className="text-xs" style={{ color: '#9B8FAA' }}>{t('notifications.enabling')}</p>}
      {device === 'on' && (
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs" style={{ color: '#2E7D32' }}>{t('notifications.enabled')}</p>
          <button
            type="button"
            onClick={() => void disable()}
            className="text-xs px-3 py-1.5 rounded-full hover:opacity-80"
            style={{ background: 'none', border: '1.5px solid #E4D5EE', color: '#6B5F7A', cursor: 'pointer' }}
          >
            {t('notifications.disable')}
          </button>
        </div>
      )}

      {/* Categories */}
      {prefs.length > 0 && (
        <ul className="mt-5 space-y-3" style={{ listStyle: 'none', padding: 0, margin: '1.25rem 0 0' }}>
          {prefs.map(row => (
            <li key={row.category} className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm" style={{ color: '#1C1128' }}>{t(`notifications.categories.${row.category}.label`, { defaultValue: row.category })}</p>
                <p className="text-xs mt-0.5" style={{ color: '#9B8FAA', lineHeight: 1.5 }}>{t(`notifications.categories.${row.category}.body`, { defaultValue: '' })}</p>
              </div>
              {row.transactional ? (
                <span className="text-[10px] uppercase tracking-widest shrink-0 mt-1" style={{ color: '#9B8FAA' }}>{t('notifications.alwaysOn')}</span>
              ) : (
                <label className="flex items-center gap-2 shrink-0 text-xs" style={{ color: '#9B8FAA' }}>
                  {savedCategory === row.category && <span style={{ color: '#2E7D32' }}>{t('notifications.saved')}</span>}
                  <input
                    type="checkbox"
                    checked={row.enabled}
                    onChange={e => void toggle(row.category, e.target.checked)}
                    aria-label={t(`notifications.categories.${row.category}.label`, { defaultValue: row.category })}
                    style={{ width: 16, height: 16, accentColor: '#3D1F62' }}
                  />
                </label>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
